import {
  type FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTerritoryEdit } from '~/_shared/hooks/useTerritoryEdit';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useIsDataExportAllowed } from '~/_shared/types/toolsAndFeatures/useIsDataExportAllowed';
import {
  ERASURE_KEYS, useKeyPress,
} from '~/_shared/utils/hooks/useKeyPress';
import { useBoundaryMetricsData } from '~/_shared/utils/metric/useBoundaryMetricsData';
import { getTotalNumberOfBoundaryPathsPolygons } from '~/boundary/boundary.helpers';
import { ExportReportType } from '~/exportReport/exportReportType';
import { useExportReport } from '~/exportReport/useExportReport';
import { useIsExportReportAvailable } from '~/exportReport/useIsExportReportAvailable';
import { useMapBoundaryBaseStyles } from '~/map/map/boundary/boundaryView/mapBoundaryStyles/useMapBoundaryBaseStyles';
import { useMapBoundaryExtraStyles } from '~/map/map/boundary/boundaryView/mapBoundaryStyles/useMapBoundaryExtraStyles';
import { useMapBoundaryTerritoryStyles } from '~/map/map/boundary/boundaryView/mapBoundaryStyles/useMapBoundaryTerritoryStyles';
import { useModal } from '~/modal/useModal.hook';
import { useBoundaryExportData } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryExportData';
import { useBoundaryStateItem } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryStateItem';
import { useRemoveCustomBoundary } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useRemoveCustomBoundary';
import { isDataLayersArtificialBoundaryTerritoryGroup } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/dataLayersTool.helpers';
import { getMostDetailedBoundaryMultiPolygon } from '~/sidebar/sidebarApps/rightSidebar/boundaryDetails/boundaryDetails.helpers';
import { useBoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useMapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import { useBoundaryTerritoryDetailsIsLoadingSelector } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import { useBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { deactivateActiveBoundary } from '~/store/frontendState/activeMapElements/activeMapElements.actionCreators';
import { useIsMobileScreenSelector } from '~/store/frontendState/deviceInfo/deviceInfo.selector';
import { checkIsSalesforceMap } from '~/store/mapInfo/mapInfo.helpers';
import {
  useIsConnectedLayeredMapSelector, useMapInfoDataSelector,
} from '~/store/mapInfo/mapInfo.selectors';
import {
  mapSettingsBoundariesClearIsolatedBoundaries, mapSettingsBoundariesSetIsolatedBoundaries,
} from '~/store/mapSettings/boundaries/mapSettingsBoundaries.actionCreators';
import { usePublicMapSettingsSelector } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.selectors';
import { useBoundaryLocationsFilters } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.selectors';
import {
  useIsMapEditDisabled, useIsMapPresentationalSelector,
} from '~/store/selectors/useMapInfoSelectors';
import { useOptimizedTerritoriesFeatureBlocker } from '../../mapTools/boundary/activeBoundary/boundaryItem/useOptimizedTerritoriesFeatureBlocker';
import { useBoundaryEditPolygon } from '../../mapTools/boundary/edit/useBoundaryEditPolygon';
import { useBoundaryBatchEditLocations } from '../../mapTools/boundary/hooks/useBoundaryBatchEditLocations';
import { useBoundaryDirections } from '../../mapTools/boundary/hooks/useBoundaryDirections';
import { useSidebarIndicatorStyles } from '../useSidebarIndicatorStyles';
import { BoundaryDetailsComponent } from './boundaryDetails.component';
import { useBoundaryDetailsMarkersCount } from './useBoundaryDetailsMarkersCount';

type BoundaryDetailsContainerProps = {
  boundaryGroupId: number;
  boundaryId: number;
  boundaryTerritoryGroupId: number;

  onClose: () => void;
};

export const BoundaryDetailsContainer: FC<BoundaryDetailsContainerProps> = (props) => {
  const dispatch = useDispatch();
  const mapInfo = useMapInfoDataSelector();
  const isBoundaryTerritoryDetailsLoading = useBoundaryTerritoryDetailsIsLoadingSelector();
  const isMapPresentational = useIsMapPresentationalSelector();
  const isEditModeDisabled = useIsMapEditDisabled();
  const publicMapSettings = usePublicMapSettingsSelector();
  const isBoundaryExportLocationsAllowed = useIsDataExportAllowed(MapTool.Boundary);
  const isLayeredMapConnected = useIsConnectedLayeredMapSelector();
  const boundaryLocationsFilters = useBoundaryLocationsFilters();
  const { getBoundaryStateItem } = useBoundaryStateItem();
  const { boundaryIsOptimizedTerritoriesTrial, blockWhenOptimizedTerritoriesTrial } = useOptimizedTerritoriesFeatureBlocker(props.boundaryGroupId);
  const { exportBoundaryData } = useBoundaryExportData();
  const { addBoundaryDirections } = useBoundaryDirections();
  const { removeCustomBoundary, isLoading: isCustomBoundaryRemoveLoading } = useRemoveCustomBoundary();
  const { isEditingBoundaryPath } = useBoundaryEditPolygon();
  const { batchEditLocationsInBoundary } = useBoundaryBatchEditLocations();
  const { openModal: openBoundaryItemSettingsModal } = useModal('BoundaryItemSettings');
  const { openModal: openExportContainedBoundaryDataModal } = useModal('ExportContainedBoundaryData');
  const { openModal: openBoundaryEditModal } = useModal('BoundaryEdit');
  const { openModal: openBoundaryCustomizeMetricsModal } = useModal('BoundaryCustomizeMetrics');
  const { openExportReport } = useExportReport();
  const startTerritoryEdit = useTerritoryEdit(props.boundaryTerritoryGroupId);
  const baseStyles = useMapBoundaryBaseStyles();
  const boundaryTerritoryStyles = useMapBoundaryTerritoryStyles(baseStyles);
  const { boundaryExtraStyles } = useMapBoundaryExtraStyles(baseStyles, boundaryTerritoryStyles);
  const isMobileScreen = useIsMobileScreenSelector();
  const boundaryPermissions = useBoundaryGroupPermissions(props.boundaryGroupId);
  const boundaryPolygons = useMapBoundariesSelector();
  const { isExportReportAvailable } = useIsExportReportAvailable();

  // Showing details for all boundaries in the group is only supported for custom boundary groups (performance).
  const canShowEntireGroupDetails = boundaryPermissions.boundaryIsCustom;
  const [isShowingEntireGroup, setIsShowingEntireGroup] = useState(false);

  useEffect(() => {
    // Reset to show single boundary details whenever the active boundary group changes.
    setIsShowingEntireGroup(false);
  }, [props.boundaryGroupId]);

  const isShowingEntireGroupDetails = canShowEntireGroupDetails && isShowingEntireGroup;

  const closeActiveBoundary = useCallback(() => {
    dispatch(deactivateActiveBoundary());
  }, [dispatch]);

  const boundaryTerritoryGroup = useBoundaryTerritoryGroupsSelector()
    .find(group => group.boundaryTerritoryGroupId === props.boundaryTerritoryGroupId);

  const boundary = useMemo(() => {
    return getBoundaryStateItem(props.boundaryId, props.boundaryGroupId);
  }, [getBoundaryStateItem, props.boundaryId, props.boundaryGroupId]);

  const { metricsResults, isLoading: isBoundaryMetricsLoading } = useBoundaryMetricsData({
    boundaryGroupId: props.boundaryGroupId,
    boundaryId: props.boundaryId,
    boundaryTerritoryGroupId: props.boundaryTerritoryGroupId,
  }, boundaryTerritoryGroup?.settings.ignoreFilters, isShowingEntireGroupDetails);

  useEffect(() => {
    if (!boundaryTerritoryGroup || !boundary) {
      closeActiveBoundary();
    }
  });

  const isBoundaryIsolated = useMemo(() => (
    boundaryLocationsFilters.isolated.get(props.boundaryGroupId)?.filteredBoundaries.includes(props.boundaryId)
  ), [boundaryLocationsFilters.isolated, props.boundaryGroupId, props.boundaryId]);

  const boundaryName = boundary?.displayName;

  const boundaryIds = useMemo(() => (
    isShowingEntireGroupDetails
      ? Array.from(boundaryPolygons.get(props.boundaryGroupId)?.keys() ?? [])
      : [props.boundaryId]
  ), [isShowingEntireGroupDetails, boundaryPolygons, props.boundaryGroupId, props.boundaryId]);

  const { markersCount: detailsMarkersCount } = useBoundaryDetailsMarkersCount(props.boundaryTerritoryGroupId, boundaryIds);
  const markersCount = detailsMarkersCount ?? metricsResults.spreadsheetColumnMetrics.rowsCount ?? null;

  const openBoundaryItemSettings = () => {
    openBoundaryItemSettingsModal({
      boundaryId: props.boundaryId,
      boundaryGroupId: props.boundaryGroupId,
      boundaryTerritoryGroupId: props.boundaryTerritoryGroupId,
    });
  };

  const { boundaryIsCustom, boundaryIsEditable } = boundaryPermissions;
  const onBoundaryItemSettingsClick = (!isEditModeDisabled && boundaryIsCustom) ? openBoundaryItemSettings : undefined;

  const handleRemoveBoundary = useMemo(() => {
    if (!isEditModeDisabled && boundaryIsEditable) {
      return () => removeCustomBoundary(props.boundaryId, props.onClose);
    }

    return undefined;
  }, [boundaryIsEditable, isEditModeDisabled, props.boundaryId, props.onClose, removeCustomBoundary]);

  const onEditBoundaryPolygonClick = (!isEditModeDisabled && boundaryIsEditable)
    ? () => startTerritoryEdit(props.boundaryId)
    : undefined;

  const onExportDataClick = isBoundaryExportLocationsAllowed
    ? () => exportBoundaryData(props.boundaryTerritoryGroupId, props.boundaryId)
    : undefined;

  const onExportContainedBoundariesClick = !isMapPresentational && boundaryTerritoryGroup
    ? () => openExportContainedBoundaryDataModal({
      filter: {
        type: 'boundary',
        boundaryGroupId: props.boundaryGroupId,
        boundaryIds: [props.boundaryId],
        matchings: boundaryTerritoryGroup.matchings,
      },
    }) : undefined;

  const onBatchEditLocations = !isEditModeDisabled && !checkIsSalesforceMap(mapInfo)
    ? () => batchEditLocationsInBoundary(props.boundaryTerritoryGroupId, props.boundaryId)
    : undefined;

  const onDirectionsClick = !isMapPresentational || publicMapSettings.routingDirections ?
    () => addBoundaryDirections(props.boundaryTerritoryGroupId, props.boundaryId)
    : undefined;

  const isMetricsEditAllowed = !isEditModeDisabled && boundaryTerritoryGroup && !isDataLayersArtificialBoundaryTerritoryGroup(boundaryTerritoryGroup);

  const onEditMetrics = useMemo(() => isMetricsEditAllowed ? () => openBoundaryCustomizeMetricsModal({
    boundaryTerritoryGroupId: props.boundaryTerritoryGroupId,
    isBoundaryGroupCustom: boundaryIsCustom,
  }) : undefined, [props.boundaryTerritoryGroupId, boundaryIsCustom, isMetricsEditAllowed, openBoundaryCustomizeMetricsModal]);

  const onIsolateBoundaryClick = useMemo(() => !isMapPresentational ? () => {
    if (isBoundaryIsolated) {
      dispatch(mapSettingsBoundariesClearIsolatedBoundaries());
    }
    else {
      dispatch(mapSettingsBoundariesSetIsolatedBoundaries(props.boundaryGroupId, {
        filteredBoundaries: [props.boundaryId],
        filteredBoundaryTerritories: [],
      }));
    }
  } : undefined, [dispatch, isBoundaryIsolated, isMapPresentational, props.boundaryGroupId, props.boundaryId]);

  const onAdvanceEditClick = useMemo(() => !isEditModeDisabled && boundaryIsEditable ?
    () => openBoundaryEditModal({
      boundaryTerritoryGroupId: props.boundaryTerritoryGroupId,
      boundaryId: props.boundaryId,
    }) : undefined
  , [boundaryIsEditable, isEditModeDisabled, openBoundaryEditModal, props.boundaryId, props.boundaryTerritoryGroupId]);

  const activeBoundaryStyles = useMemo(() => {
    if (!boundary) {
      return undefined;
    }

    const extraStylesForBoundary = boundaryExtraStyles.get(boundary.id);

    if (!extraStylesForBoundary) {
      return undefined;
    }

    const fillColor = extraStylesForBoundary.style.fillColor;
    const fillOpacity = extraStylesForBoundary.style.fillOpacity.value;
    const borderWidth = extraStylesForBoundary.style.borderWidth;
    const borderColor = extraStylesForBoundary.style.borderColor;

    return {
      fillColor,
      fillOpacity,
      borderWidth,
      borderColor,
    };
  }, [boundary, boundaryExtraStyles]);

  const indicatorStyles = useSidebarIndicatorStyles(activeBoundaryStyles);

  useKeyPress(useMemo(() => ({
    callbacks: { onKeyPress: handleRemoveBoundary },
    targetKeys: ERASURE_KEYS,
  }), [handleRemoveBoundary]));

  const onViewSummaryClick = useCallback(() => {
    if (!boundary) {
      return;
    }

    const paths = getMostDetailedBoundaryMultiPolygon(boundary.paths);

    if (!paths) {
      return;
    }

    openExportReport({
      type: ExportReportType.Polygon,
      data: {
        heading: boundaryName || '',
        paths,
      },
    });
  }, [boundary, boundaryName, openExportReport]);

  return (
    <BoundaryDetailsComponent
      boundaryName={boundaryName}
      boundaryPolygonsTotal={boundary ? getTotalNumberOfBoundaryPathsPolygons(boundary.paths) : 0}
      boundaryPermissions={boundaryPermissions}
      isArtificial={!!boundary?.settings.isArtificial}
      boundaryIsOptimizedTerritoriesTrial={boundaryIsOptimizedTerritoriesTrial}
      blockWhenOptimizedTerritoriesTrial={blockWhenOptimizedTerritoriesTrial}
      isEditingBoundary={isEditingBoundaryPath}
      isBoundaryIsolated={isBoundaryIsolated}
      isLoading={isBoundaryMetricsLoading || isBoundaryTerritoryDetailsLoading || isCustomBoundaryRemoveLoading}
      markersCount={markersCount}
      metrics={metricsResults}
      ignoreFilters={boundaryTerritoryGroup?.settings.ignoreFilters ?? false}
      onClose={closeActiveBoundary}
      onEditBoundaryPolygonClick={onEditBoundaryPolygonClick}
      isMobileVersion={isMobileScreen}
      isEditModeDisabled={isEditModeDisabled}
      onBoundaryItemSettingsClick={onBoundaryItemSettingsClick}
      onExportLocationsClick={onExportDataClick}
      onExportContainedBoundariesClick={onExportContainedBoundariesClick}
      onBoundaryItemRemoveClick={handleRemoveBoundary}
      onBatchEditLocationsClick={onBatchEditLocations}
      onDirectionsClick={onDirectionsClick}
      onIsolateBoundaryClick={onIsolateBoundaryClick}
      isLayeredMapConnected={isLayeredMapConnected}
      onAdvanceEditClick={onAdvanceEditClick}
      onCustomizeMetricsClick={onEditMetrics}
      indicatorStyles={indicatorStyles}
      canShowEntireGroupDetails={canShowEntireGroupDetails}
      isShowingEntireGroupDetails={isShowingEntireGroupDetails}
      onChangeShowEntireGroupDetails={setIsShowingEntireGroup}
      onViewSummaryClick={isExportReportAvailable ? onViewSummaryClick : undefined}
    />
  );
};
